"use client";

import { useLayoutEffect, useRef, useState } from "react";

export function useElementWidth<T extends HTMLElement>(fallbackWidth = 320) {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallbackWidth);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    const parent = element.parentElement;
    let raf1 = 0;
    let raf2 = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const measureWidth = () => {
      const ownWidth = element.getBoundingClientRect().width;
      const parentWidth = parent?.getBoundingClientRect().width ?? 0;
      const nextWidth = Math.max(ownWidth, parentWidth);
      if (nextWidth > 0) {
        setWidth(Math.floor(nextWidth));
      }
    };

    measureWidth();

    const observer = new ResizeObserver(() => {
      measureWidth();
    });

    observer.observe(element);
    if (parent) {
      observer.observe(parent);
    }

    // Hard reloads can mount charts before the surrounding layout fully settles.
    // Re-measure across a couple of frames so we capture the final available width.
    raf1 = requestAnimationFrame(() => {
      measureWidth();
      raf2 = requestAnimationFrame(() => {
        measureWidth();
      });
    });
    timeoutId = setTimeout(() => {
      measureWidth();
    }, 120);

    window.addEventListener("load", measureWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("load", measureWidth);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return { ref, width };
}
